// Follows "HR Design rules.md": GEN-07
import React from 'react';
import { CircleAlert, CircleCheck, CircleDashed, CircleDot, Clock, type LucideIcon } from 'lucide-react';
import { Badge } from '@/components/ui-kit/badge';
import { cn } from '@/lib/utils';

export type StatusTone = 'warning' | 'success' | 'error' | 'info' | 'muted';

/**
 * The subtle badge colours of each tone (DESIGN_SYSTEM.md › HR status mapping), for a `Badge` that needs a
 * tone without the chip. `info` and `muted` are both neutral; their icon tells them apart.
 */
export const TONE_STYLE: Record<StatusTone, string> = {
  warning: 'border-warning-border bg-warning-subtle text-warning-subtle-foreground',
  success: 'border-success-border bg-success-subtle text-success-subtle-foreground',
  error: 'border-destructive-border bg-destructive-subtle text-destructive-subtle-foreground',
  info: 'border-info-border bg-info-subtle text-info-subtle-foreground',
  muted: 'border-info-border bg-info-subtle text-info-subtle-foreground',
};

const TONE_VARIANT = {
  warning: 'warning-subtle',
  success: 'success-subtle',
  error: 'destructive-subtle',
  info: 'info-subtle',
  muted: 'info-subtle',
} as const;

/** Status colour is never the only cue (GEN-07): Pending a clock, Approved a check, Rejected an alert. */
const TONE_ICON: Record<StatusTone, LucideIcon> = {
  warning: Clock,
  success: CircleCheck,
  error: CircleAlert,
  info: CircleDot,
  muted: CircleDashed,
};

/** The usual tone of a request status (pending, approved, rejected, withdrawn, ...). */
const STATUS_TONE: Record<string, StatusTone> = {
  pending: 'warning',
  requested: 'warning',
  inprogress: 'info',
  approved: 'success',
  completed: 'success',
  submitted: 'success',
  rejected: 'error',
  declined: 'error',
  withdrawn: 'muted',
  cancelled: 'muted',
  canceled: 'muted',
  archived: 'muted',
};

export const statusTone = (status: string | undefined): StatusTone =>
  STATUS_TONE[(status ?? '').replace(/[\s_-]/g, '').toLowerCase()] ?? 'muted';

interface StatusChipProps {
  /** The tone; by default the usual one of `status` (see `statusTone`). */
  tone?: StatusTone;
  status?: string;
  /**
   * Show the tone's icon. By default a status tone (warning, success, error) always has one, and a neutral
   * tone has one when it comes from a `status`; a neutral label such as a category shows none.
   */
  icon?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * A status as a subtle badge with its icon and label. Long text is cut to the chip's width (the app-wide
 * `OverflowTooltip` shows it in full).
 */
export const StatusChip = ({ tone, status, icon, children, className }: Readonly<StatusChipProps>) => {
  const resolved = tone ?? statusTone(status);
  const showIcon = icon ?? (resolved !== 'info' && resolved !== 'muted' ? true : status !== undefined);
  const Icon = TONE_ICON[resolved];
  return (
    <Badge variant={TONE_VARIANT[resolved]} className={cn('max-w-full', className)}>
      {showIcon && <Icon aria-hidden="true" />}
      <span className="truncate">{children}</span>
    </Badge>
  );
};
